import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { Linking, ScrollView, View } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import Gap from "../../ui_atoms/Gap";
import { GET_WORKER_INFO, REMOVE_WORKER_FROM_CLIENT } from "../../graphql/core";
import { CvSource } from "../../__generated__/graphql";
import { WorkerInfoFieldsFragment } from "../../__generated__/operations/graphql";
import { DisplayableDocument } from "../../contexts/DocumentManager";
import DocumentEditorModal from "../../components/document/DocumentEditorModal";
import ListItem from "../../ui_atoms/ListItem";
import UserAvatar from "../user/UserAvatar";
import ListHeader from "../../ui_atoms/ListHeader";
import ListTitle from "../../ui_atoms/ListTitle";
import { useNavigation } from "@react-navigation/native";
import { ClientWorkersTabParamList } from "../../navigations/client/WorkersNavigation";
import { useSnackbar } from "../../hooks/snackbar";
import { sanitizeUrl } from "../../utils";
import { useSetAlert } from "../../hooks/use-set-alert";
import { GET_CLIENT_WORKERS_AND_INVITATION_WORKERS } from "../../graphql/client";
import ScreenHeader from "../user/ScreenHeader";

const WorkerHeader: React.FC<{ workerInfo: WorkerInfoFieldsFragment }> = ({ workerInfo }) => {
    return (
        <View style={{ width: "100%", alignItems: "center" }}>
            <UserAvatar avatarPath={workerInfo.avatarUrl} size={100} />
        </View>
    );
};

type ScreenParams = StackScreenProps<RootStackParamList & ClientWorkersTabParamList>;
type Props = StackScreenProps<ClientWorkersTabParamList, "WorkerDetails">;

const WorkerDetailsScreen: React.FC<Props> = ({ route }) => {
    const i18n = useI18n();
    const { setAlert } = useSetAlert();

    const navigation = useNavigation<ScreenParams["navigation"]>();
    const { loading, error, data } = useQuery(GET_WORKER_INFO, {
        variables: { workerId: route?.params?.workerId || "" },
        fetchPolicy: "cache-and-network",
    });
    const [removeWorkerFromClient, { loading: removing }] = useMutation(REMOVE_WORKER_FROM_CLIENT, {
        variables: { workerProfileId: route?.params?.workerId || "" },
        refetchQueries: [GET_CLIENT_WORKERS_AND_INVITATION_WORKERS],
    });
    const [document, setDocument] = useState<DisplayableDocument | null>(null);
    const workerInfo = useMemo(() => data?.getWorkerInfo, [data]);
    useEffect(() => {
        navigation.setOptions({
            header: () => (
                <ScreenHeader
                    useSmallLabel
                    onLeftAction={() => navigation.goBack()}
                    leftIcon="back"
                    label={workerInfo?.name || ""}
                />
            ),
        });
    }, [workerInfo, navigation]);
    const missionCount = useMemo(() => workerInfo?.missionCount?.total || 0, [workerInfo]);
    const { showSnackbar } = useSnackbar();

    const handleRemoveWorker = useCallback(() => {
        setAlert({
            title: i18n.t("worker_details.remove_worker_title"),
            message: i18n.t("worker_details.remove_worker_description"),
            buttons: [
                {
                    text: i18n.t("worker_details.remove_confirm"),
                    onPress: () => {
                        void removeWorkerFromClient().then(() => {
                            showSnackbar({
                                text: i18n.t(`worker_details.remove_worker_done`),
                                mode: "success",
                            });
                            navigation.popToTop();
                        });
                    },
                    style: "destructive",
                },
                {
                    text: i18n.t("worker_details.remove_cancel"),
                    style: "cancel",
                },
            ],
            options: { cancelable: true },
        });
    }, [i18n, navigation, removeWorkerFromClient, setAlert, showSnackbar]);

    const canRemoveTalent = useMemo(() => {
        if (!workerInfo?.missionCount) {
            return true;
        }
        return (
            workerInfo.missionCount.accepted +
                workerInfo.missionCount.pendingAcceptation +
                workerInfo.missionCount.terminated ===
            0
        );
    }, [workerInfo?.missionCount]);

    return (
        <LoadingBoundary loading={loading && !data} error={error}>
            {workerInfo && (
                <ScrollView>
                    <VStack>
                        <Gap gap={1} />
                        <WorkerHeader workerInfo={workerInfo} />
                        <Gap gap={2} />
                        <ListTitle value={i18n.t("worker_details.title")} />
                        <ListItem
                            key="name"
                            avatarPath={workerInfo?.avatarUrl}
                            description={workerInfo.name}
                        />
                        <ListItem
                            key="missions"
                            icon="mission"
                            description={i18n.t("worker_details.mission_count", {
                                count: missionCount,
                            })}
                            rightActionMode="none"
                        />
                        <ListItem
                            key="phone"
                            icon="phone-call"
                            description={workerInfo.phoneNumber || ""}
                            onPress={() => {
                                if (workerInfo.phoneNumber) {
                                    void Linking.openURL(`tel:${workerInfo.phoneNumber}`);
                                }
                            }}
                            rightIcon="phone-call"
                            rightActionMode="action"
                        />

                        <ListItem
                            key="email"
                            icon="email"
                            description={workerInfo.email || ""}
                            onPress={() => {
                                if (workerInfo.email) {
                                    void Linking.openURL(`mailto:${workerInfo.email}`);
                                }
                            }}
                            rightIcon="external-link"
                            rightActionMode="action"
                        />
                        {!!workerInfo.curriculumVitae && (
                            <ListItem
                                key="qualifications"
                                {...(() => {
                                    switch (workerInfo.curriculumVitae.source) {
                                        case CvSource.Internal:
                                            return {
                                                icon: "badge-check",
                                                description: i18n.t(
                                                    "worker_details.qualifications",
                                                ),
                                                rightActionMode: "navigate",
                                                onPress: () => {
                                                    navigation?.navigate("WorkerQualifications", {
                                                        workerId: workerInfo.id,
                                                    });
                                                },
                                            };
                                        case CvSource.LinkedinLink:
                                            return {
                                                icon: "link",
                                                description:
                                                    workerInfo.curriculumVitae.link?.replace(
                                                        "https://",
                                                        "",
                                                    ),
                                                rightIcon: "arrow-up-right-from-square",
                                                rightActionMode: "action_simple",
                                                onPress: () => {
                                                    if (workerInfo.curriculumVitae?.link) {
                                                        const sanitizedUrl = sanitizeUrl(
                                                            workerInfo.curriculumVitae.link,
                                                        );
                                                        if (sanitizedUrl) {
                                                            void Linking.openURL(sanitizedUrl);
                                                        }
                                                    }
                                                },
                                            };
                                        case CvSource.PdfDocument:
                                            return {
                                                icon: "file-lines",
                                                description: i18n.t(
                                                    "worker_details.curriculum_vitae_pdf",
                                                ),
                                                rightActionMode: "navigate",
                                                onPress: () => {
                                                    const documentTitle = `${i18n.t(
                                                        "worker_details.curriculum_vitae_pdf",
                                                    )} - ${workerInfo.name}`;
                                                    navigation?.navigate("PdfViewer", {
                                                        title: documentTitle,
                                                        filename: documentTitle,
                                                        assetUrl:
                                                            workerInfo.curriculumVitae
                                                                ?.pdfDocumentUrl || "",
                                                    });
                                                },
                                            };
                                        case CvSource.Empty:
                                            return {
                                                icon: "badge-check",
                                                description: i18n.t(
                                                    "worker_details.without_qualifications",
                                                ),
                                                rightActionMode: "none",
                                            };
                                    }
                                })()}
                            />
                        )}
                        {!!workerInfo.documents?.length && (
                            <>
                                <Gap gap={1} />
                                <ListHeader value={i18n.t("worker_details.documents_title")} />
                                {workerInfo.documents.map((document) => {
                                    return (
                                        <ListItem
                                            key={document.id}
                                            icon="file"
                                            title={document.title}
                                            onPress={() => setDocument(document)}
                                        />
                                    );
                                })}
                            </>
                        )}
                        <Gap gap={1} />
                        <ListItem
                            labels={[
                                {
                                    text: i18n.t("worker_details.create_mission_action", {
                                        name: workerInfo.name,
                                    }),
                                    mode: "cta",
                                },
                            ]}
                            onPress={() => {
                                navigation.push("CreateMissionStack", {
                                    initialMission: { workerInfo },
                                });
                            }}
                            rightActionMode="action"
                        />
                        <ListItem
                            labels={[
                                {
                                    text: i18n.t("worker_details.list_missions_action", {
                                        name: workerInfo.name,
                                    }),
                                    mode: "cta",
                                },
                            ]}
                            onPress={() => {
                                navigation.push("WorkerMissionList", {
                                    workerId: workerInfo.id,
                                });
                            }}
                            rightActionMode="action"
                        />
                        <ListItem
                            labels={[
                                {
                                    text: i18n.t("worker_details.list_timesheet_action"),
                                    mode: "cta",
                                },
                            ]}
                            onPress={() => {
                                navigation.push("WorkerTimesheetList", {
                                    workerId: workerInfo.id,
                                });
                            }}
                            rightActionMode="action"
                        />
                        <ListItem
                            labels={[
                                {
                                    text: i18n.t("worker_details.list_invoices_action"),
                                    mode: "cta",
                                },
                            ]}
                            onPress={() => {
                                navigation.push("WorkerInvoices", {
                                    workerId: workerInfo.id,
                                });
                            }}
                            rightActionMode="action"
                        />
                        {canRemoveTalent && (
                            <ListItem
                                labels={[
                                    {
                                        text: i18n.t("worker_details.remove_worker_action"),
                                        mode: "redCta",
                                    },
                                ]}
                                onPress={handleRemoveWorker}
                                rightActionMode="empty"
                                disabled={removing}
                            />
                        )}
                    </VStack>
                    <Gap gap={2} />
                </ScrollView>
            )}
            {document && (
                <DocumentEditorModal
                    visible={!!document}
                    document={document}
                    onClose={() => {
                        setDocument(null);
                    }}
                />
            )}
        </LoadingBoundary>
    );
};

export default WorkerDetailsScreen;
